// Profil-Nachbau F-25 (PF-GR-saetze-1; output/profil-team-20261014/cockpit-grafiken-20261019/SCHNITTSTELLEN.md 5.3 und 7a, Entwurf A
// „Bericht in Sätzen“): die Satzregeln des KI-Cockpits – Hauptsatz und Folgesätze der Übersicht (Firma, Team, ohne eigenes Team,
// Plattform), Quellen-Satz, Wochenrhythmus und die Tooltips der Bezüge. Reine Regeln: die Wörter stehen in cockpitTexte.ts BERICHT,
// ein Bezug dort als ⟦…⟧. Diese Datei wählt die Vorlage, setzt die Zahlen ein und schneidet an den Marken. Kein React, kein Betrag
// (die Plattform gibt fertige Beträge herein), kein Zufall, kein fester Produktname.
import { BERICHT, KENNZAHL, QUELLEN, SO_RECHNET } from './cockpitTexte';
import { datum, dauer, monatName, tagKurz, trendText, zahl, zahlEineStelle } from './format';
import type { Einheit, QuellenArt, QuellenStand } from './cockpitApi';
import type { Vorgabe } from './zeitraum';

export type Bezug =
  | { art: 'recherchen' } | { art: 'nachfragen' } | { art: 'vorher' } | { art: 'aktiv' } | { art: 'zeit' }
  | { art: 'hoechst'; index: number } | { art: 'wochentag'; tag: number /* 0 = Montag */ }
  | { art: 'quelle'; quelle: QuellenArt | 'OHNE' } | { art: 'firmen' } | { art: 'kosten' }
  | { art: 'stufe'; index: number } | { art: 'tipp'; kopf: string; text: string };
export type Satzteil = string | { text: string; bezug: Bezug; stark?: boolean };
export type Satz = Satzteil[];
export type Wer = { art: 'firma' } | { art: 'team'; name: string } | { art: 'ohneTeam' } | { art: 'plattform' };
export type BerichtAbschnitt = { von: string; bis: string; recherchen: number; nachfragen: number; aktivePersonen: number };
export type BerichtLage = {
  zeitraum: { von: string; bis: string; tage: number; vorgabe: Vorgabe | null };
  heute: string; wer: Wer; einheit: Einheit; abschnitte: BerichtAbschnitt[];
  recherchen: number; nachfragen: number; aktivePersonen: number; personen: number;
  minuten?: number;
  vergleich?: { von?: string; bis?: string; tage: number; recherchen: number; nachfragen: number; aktivePersonen: number };
  zeitRegel?: { minutenJeRecherche: number; minutenJeNachfrage: number };
  produkt: string;
};
export type Wochentag = { tag: number; tage: number; recherchen: number; nachfragen: number; schnittRecherchen: number; schnittNachfragen: number };
/** Gleiche Form wie TippInhalt in Diagramm.tsx (PF-GR-cockpit-1), hier ohne Import, damit dieses Paket allein baut. */
export type BerichtTipp = { kopf: string; text?: string; zeilen?: { farbe?: 'recherchen' | 'nachfragen' | 'ohneQuelle' | 'linie'; name: string; wert: string }[]; fuss?: string };
/** Fertig formatierte Beträge der Plattform (netMoney); diese Datei formatiert keinen Betrag. */
export type BerichtGeld = { kosten: string; kostenDavor?: string; jeRecherche?: string; umsatz: string; marge: string; umsatzNull: boolean };

type Zeitraum = BerichtLage['zeitraum'];
type Sichtbar = { recherchen: boolean; nachfragen: boolean };
type TippZeile = NonNullable<BerichtTipp['zeilen']>[number];

// ---- Hilfen

/** Die Marken eines Bezugs in den Vorlagen (U+27E6, U+27E7). */
const MARKE = /[⟦⟧]/;
const MARKEN = /[⟦⟧]/g;
/** Nutzertext (Teamname, Marke) ohne Marken, damit er nie als Bezug gelesen wird. */
const ohneMarken = (text: string) => text.replace(MARKEN, '');

/** Hängt ein Stück an; zwei Texte hintereinander werden ein Stück. */
function anhaengen(satz: Satz, teil: Satzteil): void {
  const letztes = satz[satz.length - 1];
  if (typeof teil === 'string' && typeof letztes === 'string') satz[satz.length - 1] = letztes + teil;
  else satz.push(teil);
}

/** Schneidet eine Vorlage an ⟦…⟧: der n-te markierte Text bekommt bezuege[n]; leere Stücke entfallen. */
function teile(vorlage: string, bezuege: Bezug[], stark: boolean): Satz {
  const satz: Satz = [];
  vorlage.split(MARKE).forEach((stueck, i) => {
    if (!stueck) return;
    const bezug = i % 2 === 1 ? bezuege[(i - 1) / 2] : undefined;
    if (!bezug) anhaengen(satz, stueck);
    else satz.push(stark ? { text: stueck, bezug, stark: true } : { text: stueck, bezug });
  });
  return satz;
}

/** Stücke eines Satzes ohne Zwischenraum aneinander. */
function verketten(...stuecke: Satz[]): Satz {
  const satz: Satz = [];
  for (const stueck of stuecke) for (const teil of stueck) anhaengen(satz, teil);
  return satz;
}

/** Sätze mit einem Leerzeichen getrennt; leere entfallen, am Ende kein Leerzeichen. */
function verbinden(saetze: Satz[]): Satz {
  const satz: Satz = [];
  for (const einer of saetze) {
    if (einer.length === 0) continue;
    if (satz.length > 0) anhaengen(satz, ' ');
    for (const teil of einer) anhaengen(satz, teil);
  }
  return satz;
}

/** Wochentag eines Tags JJJJ-MM-TT, Montag = 0, ohne Ortszeit. */
const wochentagVon = (tag: string) =>
  (new Date(Date.UTC(Number(tag.slice(0, 4)), Number(tag.slice(5, 7)) - 1, Number(tag.slice(8, 10)))).getUTCDay() + 6) % 7;

/** Änderung in ganzen Prozent wie trend am Server (BE/kennzahlen.ts); −0 gilt als 0. */
function prozentAenderung(jetzt: number, davor: number): number {
  const p = Math.round((jetzt - davor) * 100 / davor);
  return p === 0 ? 0 : p;
}
const prozentText = (p: number) => `${zahl(p)} %`;

function davorText(z: Zeitraum): string {
  if (z.vorgabe === 'heute') return BERICHT.vergleich.gestern;
  return z.tage === 1 ? BERICHT.vergleich.tag : BERICHT.vergleich.tage(z.tage);
}

function werWorte(wer: Wer): { nominativ: string; verb: string; dativ: string } {
  if (wer.art === 'team') return BERICHT.wer.team(ohneMarken(wer.name));
  return wer.art === 'ohneTeam' ? BERICHT.wer.ohneTeam : BERICHT.wer.firma;
}

const bisher = (z: Zeitraum) => laeuftNoch(z) ? BERICHT.zeit.bisher : '';

/** Keine Reihe sichtbar zählt wie beide (die Legende lässt die letzte ohnehin nicht abwählen). */
const reihenWahl = (sichtbar: Sichtbar): Sichtbar => sichtbar.recherchen || sichtbar.nachfragen ? sichtbar : { recherchen: true, nachfragen: true };
const rhythmusWort = (s: Sichtbar): string =>
  s.recherchen && s.nachfragen ? BERICHT.rhythmus.wort.beide : s.recherchen ? BERICHT.rhythmus.wort.recherchen : BERICHT.rhythmus.wort.nachfragen;
const schnittVon = (t: Wochentag, s: Sichtbar) => (s.recherchen ? t.schnittRecherchen : 0) + (s.nachfragen ? t.schnittNachfragen : 0);

/** Arten nach Anteil absteigend; bei Gleichstand in der Reihenfolge der Antwort (sort ist stabil). */
const geordnet = (stand: QuellenStand) => [...stand.arten].sort((a, b) => (b.prozent ?? 0) - (a.prozent ?? 0));
const anteilDavor = (davor: QuellenStand, art: QuellenArt) => davor.arten.find(a => a.art === art)?.prozent ?? 0;

// ---- Kleine Ausgänge

/** Teile aneinander, Bezüge als ihr Text (Vorlesehilfen, Tests). */
export function satzText(satz: Satz): string {
  return satz.map(teil => typeof teil === 'string' ? teil : teil.text).join('');
}

/** „Dienstag, 13. Oktober“ (mit „Jänner“). */
export function tagSatz(tag: string): string {
  return BERICHT.tagSatz(BERICHT.wochentage[wochentagVon(tag)], Number(tag.slice(8, 10)), monatName(tag));
}

/** Der Zeitraum läuft noch (dieser Monat, dieses Jahr): „bisher“. */
export function laeuftNoch(z: Zeitraum): boolean {
  return z.vorgabe === 'dieser-monat' || z.vorgabe === 'dieses-jahr';
}

/** Zeitangabe am Satzanfang nach Entwurf A (Übersicht der Firma): „In den letzten 30 Tagen“, „Im Oktober“, „2026“, „Vom … bis …“. */
export function zeitText(z: Zeitraum): string {
  const v = z.vorgabe;
  if (v !== null && v in BERICHT.zeit.fest) return BERICHT.zeit.fest[v];
  if (v === 'dieser-monat' || v === 'letzter-monat') return BERICHT.zeit.monat(monatName(z.von));
  if (v === 'dieses-jahr') return BERICHT.zeit.jahr(z.von.slice(0, 4));
  if (z.von === z.bis) return BERICHT.zeit.einTag(datum(z.von));
  return z.von.slice(0, 4) === z.bis.slice(0, 4) ? BERICHT.zeit.eigen(tagKurz(z.von), datum(z.bis)) : BERICHT.zeit.eigen(datum(z.von), datum(z.bis));
}

/** Zeitangabe der übrigen Seiten und der Plattform (Entwurf mittel\seiten.js zeitText): „Im Oktober bisher“, „Seit Jahresbeginn“. */
export function zeitTextSeite(z: Zeitraum): string {
  if (z.vorgabe === 'dieser-monat') return BERICHT.zeit.monatBisher(monatName(z.von));
  if (z.vorgabe === 'dieses-jahr') return BERICHT.zeit.seitJahresbeginn;
  return zeitText(z);
}

/** Vergleich ohne Bezug („6 % mehr als in den 30 Tagen davor“); null bei „Dieses Jahr“ und ohne Wert davor. */
export function vergleichText(jetzt: number, davor: number, z: Zeitraum): string | null {
  if (z.vorgabe === 'dieses-jahr' || davor === 0) return null;
  const p = prozentAenderung(jetzt, davor), v = BERICHT.vergleich;
  if (p === 0) return v.textGleich;
  return p > 0 ? v.textMehr(prozentText(p), davorText(z)) : v.textWeniger(prozentText(-p), davorText(z));
}

/** Gewonnene Zeit in Arbeitstagen zu 8 Stunden: „gut 4 Arbeitstage“, „fast 2 Arbeitstage“; unter 8 Stunden null. */
export function arbeitstage(minuten: number): string | null {
  const t = minuten / 480;
  if (t < 1) return null;
  const ganz = Math.floor(t), rest = t - ganz, A = BERICHT.folge.arbeitstage;
  if (rest < 0.05) return A.genau(zahl(ganz), ganz === 1);
  if (rest < 0.5) return A.gut(zahl(ganz), ganz === 1);
  return A.fast(zahl(Math.ceil(t)));
}

/** Index des größten Werts über 0; bei Gleichstand der letzte; ohne Wert −1. */
export function hoechsterAbschnitt(abschnitte: BerichtAbschnitt[], reihe: 'recherchen' | 'nachfragen'): number {
  let index = -1, hoechst = 0;
  abschnitte.forEach((a, i) => {
    if (a[reihe] > 0 && a[reihe] >= hoechst) { hoechst = a[reihe]; index = i; }
  });
  return index;
}

// ---- Wochenrhythmus

/** Schnitt je Wochentag (Montag = 0 … Sonntag = 6) aus Abschnitten zu einem Tag, ohne den laufenden Tag. */
export function rhythmus(abschnitte: BerichtAbschnitt[], heute: string): Wochentag[] {
  const liste: Wochentag[] = Array.from({ length: 7 }, (_, tag) => ({ tag, tage: 0, recherchen: 0, nachfragen: 0, schnittRecherchen: 0, schnittNachfragen: 0 }));
  for (const a of abschnitte) {
    if (a.von !== a.bis || a.von === heute) continue;
    const t = liste[wochentagVon(a.von)];
    t.tage += 1; t.recherchen += a.recherchen; t.nachfragen += a.nachfragen;
  }
  for (const t of liste) if (t.tage > 0) { t.schnittRecherchen = t.recherchen / t.tage; t.schnittNachfragen = t.nachfragen / t.tage; }
  return liste;
}

export function rhythmusSatz(rh: Wochentag[], tage: number, sichtbar: Sichtbar): string {
  const s = reihenWahl(sichtbar), wort = rhythmusWort(s), R = BERICHT.rhythmus;
  if (rh.reduce((summe, t) => summe + t.tage, 0) < 7) return R.zuKurz;
  if (tage < 14) return R.kurz(wort);
  let beste: Wochentag | null = null, besterSchnitt = 0;
  for (const t of rh) {
    const schnitt = schnittVon(t, s);
    if (schnitt > besterSchnitt) { beste = t; besterSchnitt = schnitt; }
  }
  if (beste === null) return R.nichts;
  return R.spitze(BERICHT.wochentageAdverbGross[beste.tag], zahlEineStelle(besterSchnitt), wort);
}

/** Vorlesetext je Balken im Block „Rhythmus der Woche“ (PF-GR-cockpit-3). */
export function rhythmusZeile(t: Wochentag, sichtbar: Sichtbar): string {
  if (t.tage === 0) return BERICHT.rhythmus.ohneTag(BERICHT.wochentage[t.tag]);
  const s = reihenWahl(sichtbar);
  return BERICHT.rhythmus.zeile(BERICHT.wochentage[t.tag], zahlEineStelle(schnittVon(t, s)), rhythmusWort(s), t.tage);
}

// ---- Übersicht der Firma (Firma, Team, ohne eigenes Team)

function vergleichsteil(lage: BerichtLage): string {
  const z = lage.zeitraum, V = lage.vergleich, v = BERICHT.vergleich;
  if (z.vorgabe === 'dieses-jahr' || !V) return '';
  if (V.recherchen === 0) return z.vorgabe === 'heute' ? v.gesternKeine : v.keine;
  const p = prozentAenderung(lage.recherchen, V.recherchen);
  if (p === 0) return v.gleich(davorText(z));
  return p > 0 ? v.mehr(prozentText(p), davorText(z)) : v.weniger(prozentText(-p), davorText(z));
}

/** H0 bis H2. */
export function hauptsatz(lage: BerichtLage): Satz {
  const z = lage.zeitraum, R = lage.recherchen, W = werWorte(lage.wer), H = BERICHT.haupt;
  if (R === 0) {
    if (z.vorgabe === 'heute') return [H.heuteKeine(W.verb, W.nominativ)];
    return [H.keine(zeitText(z), W.verb, W.nominativ, bisher(z))];
  }
  const vorlage = H.gestellt(zeitText(z), W.verb, W.nominativ, bisher(z), H.rechtsfragen(zahl(R), R === 1), vergleichsteil(lage));
  return teile(vorlage, [{ art: 'recherchen' }, { art: 'vorher' }], false);
}

/** M: wer mitgemacht hat. */
function mitmachen(lage: BerichtLage): Satz {
  const P = lage.personen, A = lage.aktivePersonen, F = BERICHT.folge;
  if (P >= 1 && A >= P) return [P === 1 ? F.einzige : F.alle(zahl(P))];
  if (A >= 1 && A < P) return teile(F.mitgemacht(zahl(A), A === 1, zahl(P), P === 1), [{ art: 'aktiv' }], true);
  return [];
}

/** „; freitags wird am meisten gefragt, am Wochenende kaum.“ ab 14 Tagen je Tag, sonst nur der Punkt. */
function wochentagTeil(lage: BerichtLage): Satz {
  if (lage.einheit === 'TAG' && lage.zeitraum.tage >= 14) {
    const rh = rhythmus(lage.abschnitte, lage.heute);
    let top: Wochentag | null = null;
    for (const t of rh.slice(0, 5)) if (t.tage > 0 && (top === null || t.schnittRecherchen > top.schnittRecherchen)) top = t;
    if (top !== null && top.schnittRecherchen > 0) {
      const kaum = rh[5].recherchen + rh[6].recherchen <= 0.1 * lage.recherchen;
      return teile(BERICHT.folge.wochentag(BERICHT.wochentageAdverb[top.tag], kaum), [{ art: 'wochentag', tag: top.tag }], true);
    }
  }
  return ['.'];
}

/** H: der höchste Abschnitt, bei Tagen ab 14 Tagen mit dem stärksten Werktag. */
function hoechstSatz(lage: BerichtLage): Satz {
  const A = lage.abschnitte, F = BERICHT.folge;
  const mi = A.length > 1 ? hoechsterAbschnitt(A, 'recherchen') : -1;
  if (mi < 0) return [];
  const a = A[mi], r = zahl(a.recherchen), eine = a.recherchen === 1;
  const vorlage = lage.einheit === 'TAG' ? F.amTag(tagSatz(a.von), r, eine)
    : lage.einheit === 'WOCHE' ? F.inWoche(tagKurz(a.von), r, eine) : F.imMonat(monatName(a.von), r, eine);
  return verketten(teile(vorlage, [{ art: 'hoechst', index: mi }], true), wochentagTeil(lage));
}

/** N: Nachfragen mit Trend zum Vorzeitraum. */
function nachfragenSatz(lage: BerichtLage): Satz {
  const N = lage.nachfragen, V = lage.vergleich;
  if (N <= 0) return [];
  const trend = lage.zeitraum.vorgabe !== 'dieses-jahr' && V && V.nachfragen > 0
    ? trendText({ art: 'PROZENT', prozent: prozentAenderung(N, V.nachfragen) }) : null;
  return [BERICHT.folge.nachfragen(zahl(N), N === 1, trend)];
}

/** Z: gewonnene Zeit; fehlt sie (Regel ausgeblendet) oder ist sie 0, kein Satz. */
function zeitSatz(lage: BerichtLage): Satz {
  const m = lage.minuten;
  if (m === undefined || m <= 0) return [];
  return teile(BERICHT.folge.zeit(ohneMarken(lage.produkt), werWorte(lage.wer).dativ, dauer(m), arbeitstage(m)), [{ art: 'zeit' }], true);
}

/** F0 bis F2. */
export function folgesaetze(lage: BerichtLage): Satz {
  const z = lage.zeitraum, N = lage.nachfragen, V = lage.vergleich, F = BERICHT.folge;
  if (lage.recherchen === 0) {
    const saetze: Satz[] = [];
    if (N > 0) saetze.push([F.nurNachfragen(zahl(N), N === 1)]);
    if (z.vorgabe === 'heute' && V) {
      saetze.push([V.recherchen + V.nachfragen === 0 ? F.gesternKeine
        : F.gestern(zahl(V.recherchen), V.recherchen === 1, zahl(V.nachfragen), V.nachfragen === 1,
          V.aktivePersonen > 0 ? zahl(V.aktivePersonen) : null, V.aktivePersonen === 1)]);
    }
    return verbinden(saetze);
  }
  return verbinden([mitmachen(lage), hoechstSatz(lage), nachfragenSatz(lage), zeitSatz(lage)]);
}

// ---- Übersicht der Plattform

export function plattformHauptsatz(lage: BerichtLage & { firmen: { aktiv: number; mitZugang: number } }): Satz {
  const { aktiv, mitZugang } = lage.firmen, z = lage.zeitraum, R = lage.recherchen, P = BERICHT.plattform;
  const produkt = ohneMarken(lage.produkt), zeit = zeitTextSeite(z);
  if (aktiv === 0) {
    if (z.vorgabe === 'heute') return [P.heuteKeine(produkt)];
    if (mitZugang === 0) return [P.keineFirma(zeit, produkt)];
    if (mitZugang === 1) return [P.keineEine(zeit, produkt)];
    return [P.keine(zeit, zahl(mitZugang), produkt)];
  }
  const vorlage = P.genutzt(zeit, aktiv === 1 ? P.verb.eine : P.verb.mehrere, P.firmen(zahl(aktiv), zahl(mitZugang), mitZugang === 1), produkt,
    R > 0 ? P.recherchen(zahl(R), R === 1) : null);
  return teile(vorlage, [{ art: 'firmen' }], false);
}

/** Vergleich, Kosten und Umsatz (mit Recht „Kostenbericht ansehen“) bzw. Nachfragen, dazu der Tarif-Filter. */
export function plattformFolge(lage: BerichtLage, geld?: BerichtGeld, tarifName?: string): Satz {
  const z = lage.zeitraum, R = lage.recherchen, N = lage.nachfragen, V = lage.vergleich, P = BERICHT.plattform;
  const saetze: Satz[] = [];
  if (z.vorgabe !== 'dieses-jahr' && V) {
    if (V.recherchen === 0 && R > 0) saetze.push([z.vorgabe === 'heute' ? P.gesternKeine : P.davorKeine]);
    else {
      const vt = vergleichText(R, V.recherchen, z);
      if (vt !== null) saetze.push([P.dasSind(vt)]);
    }
  }
  if (geld) {
    saetze.push(teile(P.kosten(geld.kosten, R > 0 && geld.jeRecherche ? geld.jeRecherche : null), [{ art: 'kosten' }], true));
    saetze.push([geld.umsatzNull ? P.umsatzNull(geld.marge) : P.umsatz(geld.umsatz, geld.marge)]);
  } else if (N > 0) saetze.push([BERICHT.folge.nachfragen(zahl(N), N === 1, null)]);
  if (tarifName) saetze.push([P.tarif(tarifName)]);
  return verbinden(saetze);
}

// ---- Quellen

/** „Drei von vier Antworten stützen sich auf Bundesrecht.“ */
export function quellenHaupt(jetzt: QuellenStand): Satz {
  const Q = BERICHT.quellen;
  if (jetzt.antworten === 0) return [Q.keine];
  const erste = geordnet(jetzt)[0];
  if (!erste || erste.antworten === 0) return [Q.keineQuelle];
  const p = erste.prozent ?? 0, a = Q.anteil;
  const anteil = p >= 100 ? a.alle : p >= 85 ? a.fastAlle : p >= 70 ? a.dreiVonVier : p >= 60 ? a.zweiVonDrei : p >= 45 ? a.jedeZweite
    : a.prozent(prozentText(p));
  return teile(Q.haupt(anteil, erste.name), [{ art: 'quelle', quelle: erste.art }], false);
}

/** Die Quelle, die deutlich öfter gebraucht wird als davor (ab Faktor 1,6 und 8 Antworten). */
export function quellenWechsel(jetzt: QuellenStand, davor: QuellenStand | null, vorgabe: Vorgabe | null): Satz {
  if (vorgabe === 'dieses-jahr' || !davor || davor.antworten === 0 || jetzt.antworten === 0) return [];
  let beste: { art: QuellenArt; name: string; p: number; pDavor: number } | null = null, besterFaktor = 0;
  for (const a of geordnet(jetzt)) {
    const pDavor = anteilDavor(davor, a.art), p = a.prozent ?? 0;
    if (a.antworten < 8 || pDavor <= 0) continue;
    const faktor = p / pDavor;
    if (beste === null || faktor > besterFaktor) { beste = { art: a.art, name: a.name, p, pDavor }; besterFaktor = faktor; }
  }
  if (beste === null || besterFaktor < 1.6) return [];
  const Q = BERICHT.quellen;
  return teile(Q.wechsel(beste.name, Q.mehrzahl.includes(beste.art), prozentText(beste.p), prozentText(beste.pDavor)),
    [{ art: 'quelle', quelle: beste.art }], true);
}

/** Übersicht (Entwurf A): Hauptsatz und Wechsel in einem. */
export function quellenSatz(jetzt: QuellenStand, davor: QuellenStand | null, vorgabe: Vorgabe | null): Satz {
  return verbinden([quellenHaupt(jetzt), quellenWechsel(jetzt, davor, vorgabe)]);
}

/** „12 Antworten (6 %) nannten keine Quelle, weniger als davor (9 %).“ (ohne Bezug) */
export function quellenOhneSatz(jetzt: QuellenStand, davor: QuellenStand | null, vorgabe: Vorgabe | null): Satz {
  const Q = BERICHT.quellen;
  if (jetzt.antworten === 0) return [];
  const n = jetzt.ohneTreffer.anzahl;
  if (n === 0) return [Q.alleMitQuelle];
  const po = jetzt.ohneTreffer.prozent ?? 0;
  let vgl: string = '';
  if (vorgabe !== 'dieses-jahr' && davor && davor.antworten > 0) {
    const pov = davor.ohneTreffer.prozent ?? 0;
    vgl = pov > po ? Q.ohneWeniger(prozentText(pov)) : pov < po ? Q.ohneMehr(prozentText(pov)) : Q.ohneGleich;
  }
  return [Q.ohne(zahl(n), n === 1, prozentText(po), vgl)];
}

// ---- Tooltips der Bezüge

/** Inhalt des Tooltips an einem Bezug; null, wo es keinen gibt (der Bezug zeigt dann nur die Grafik). */
export function bezugTipp(bezug: Bezug, lage: BerichtLage,
  extra?: { geld?: BerichtGeld; quellen?: { jetzt: QuellenStand; davor: QuellenStand | null } }): BerichtTipp | null {
  const T = BERICHT.tipp, V = lage.vergleich;
  switch (bezug.art) {
    case 'recherchen':
      return { kopf: KENNZAHL.recherchen, text: T.recherchen(zahlEineStelle(lage.recherchen / Math.max(1, lage.zeitraum.tage))) };
    case 'nachfragen':
      return { kopf: KENNZAHL.nachfragen, text: SO_RECHNET.nachfrageTipp };
    case 'vorher':
      if (!V || !V.von || !V.bis) return null;
      return { kopf: T.vorher(datum(V.von), datum(V.bis)), zeilen: [{ farbe: 'recherchen', name: KENNZAHL.recherchen, wert: zahl(V.recherchen) },
        { farbe: 'nachfragen', name: KENNZAHL.nachfragen, wert: zahl(V.nachfragen) }], fuss: T.vorherFuss };
    case 'aktiv':
      return { kopf: KENNZAHL.aktivePersonen, text: T.aktiv };
    case 'zeit': {
      const regel = lage.zeitRegel;
      if (!regel) return null;
      const satz = lage.wer.art === 'plattform' ? SO_RECHNET.zeitTippPlattform : SO_RECHNET.zeitTippFirma;
      return { kopf: KENNZAHL.zeit, text: satz(regel.minutenJeRecherche, regel.minutenJeNachfrage) };
    }
    case 'hoechst':
    case 'stufe':
      return null;
    case 'wochentag': {
      if (lage.einheit !== 'TAG') return null;
      const t = rhythmus(lage.abschnitte, lage.heute)[bezug.tag];
      if (!t || t.tage === 0) return null;
      return { kopf: BERICHT.wochentageAdverbGross[t.tag],
        text: T.wochentag(zahlEineStelle(t.schnittRecherchen), t.tage, BERICHT.wochentage[t.tag], BERICHT.wochentageMehrzahl[t.tag]) };
    }
    case 'quelle': {
      const q = extra?.quellen;
      if (!q) return null;
      const { jetzt, davor } = q, art = bezug.quelle;
      if (art === 'OHNE') return { kopf: QUELLEN.ohneTreffer, text: T.ohneQuelle(zahl(jetzt.ohneTreffer.anzahl), zahl(jetzt.antworten)) };
      const a = jetzt.arten.find(x => x.art === art);
      if (!a) return null;
      const tipp: BerichtTipp = { kopf: a.name, text: T.quelle(zahl(a.antworten), zahl(jetzt.antworten)) };
      if (davor && davor.antworten > 0) {
        tipp.zeilen = [{ farbe: 'recherchen', name: T.imZeitraum, wert: prozentText(a.prozent ?? 0) },
          { farbe: 'linie', name: T.davor, wert: prozentText(anteilDavor(davor, art)) }];
      }
      return tipp;
    }
    case 'firmen':
      return { kopf: KENNZAHL.aktiveFirmen, text: SO_RECHNET.firmenTipp };
    case 'kosten': {
      const geld = extra?.geld;
      if (!geld) return null;
      const zeilen: TippZeile[] = [{ farbe: 'recherchen', name: T.zeitraum, wert: geld.kosten }];
      if (geld.kostenDavor) zeilen.push({ farbe: 'linie', name: T.davor, wert: geld.kostenDavor });
      return { kopf: KENNZAHL.kosten, zeilen, fuss: T.kostenFuss };
    }
    case 'tipp':
      return { kopf: bezug.kopf, text: bezug.text };
  }
}
